import React, { useState, useEffect, useRef } from "react";
import {
  Plus,
  Plug,
  Edit3,
  Trash,
  Search,
  ChevronLeft,
  ChevronRight,
  X,
} from "lucide-react";
import ApiEndpointForm from "../../components/apiEndpoints/ApiEndpointForm";
import apiEndpointService from "../../services/apiEndpoint.service";
import Tooltip from "../../widgets/ToolTip";
import toast from "react-hot-toast";
import Loader from "../../widgets/Loader";
import ConfirmationDialog from "../../widgets/ConfirmationModal";
import Can from "../../components/auth/Can";
import apiSvg from "../../assets/svg/api-interface-svgrepo-com.svg";

// Type definitions
interface ApiEndpointType {
  _id: string;
  api_name: string;
  endpoint: string;
  method: "GET" | "POST" | "PUT" | "DELETE";
  headers: Record<string, string>;
  api_key?: string;
  description?: string;
  rate_limit: number;
  is_active: boolean;
  createdAt: string;
  updatedAt: string;
}

interface ApiEndpointListParams {
  skip?: number;
  limit?: number;
  api_name?: string;
}

interface CreateApiEndpointData {
  endpoint_name: string;
  base_url: string;
  api_key?: string;
  description?: string;
  is_active: boolean;
}

interface UpdateApiEndpointData extends CreateApiEndpointData {}

// List View Component
const ListView: React.FC<{
  configs: ApiEndpointType[];
  onEdit: (config: ApiEndpointType) => void;
  onDelete: (config: ApiEndpointType) => void;
}> = ({ configs, onEdit, onDelete }) => {
  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Invalid Date";
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                API Name
              </th>
              <th className="px-4 py-2 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                Base URL
              </th>
              <th className="px-4 py-2 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-2 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                Created At
              </th>
              <th className="px-4 py-2 text-right text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {configs.map((config) => (
              <tr key={config._id} className="hover:bg-gray-50">
                <td className="px-4 py-2 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="w-7 h-7 bg-gray-50 border border-gray-100 rounded-md flex items-center justify-center mr-2.5 flex-shrink-0">
                      <img
                        src={apiSvg}
                        alt="API"
                        className="w-4 h-4 object-contain"
                      />
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      {config.api_name}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                  {config.endpoint}
                </td>
                <td className="px-4 py-2 whitespace-nowrap">
                  <span
                    className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                      config.is_active
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {config.is_active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                  {formatDate(config.createdAt)}
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end gap-2">
                    <Can permission="integrations:edit">
                      <Tooltip content="Edit">
                        <button
                          onClick={() => onEdit(config)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded"
                          type="button"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                      </Tooltip>
                    </Can>
                    <Can permission="integrations:delete">
                      <Tooltip content="Delete">
                        <button
                          onClick={() => onDelete(config)}
                          className="text-red-600 hover:text-red-900 p-1 rounded"
                          type="button"
                        >
                          <Trash className="w-4 h-4" />
                        </button>
                      </Tooltip>
                    </Can>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ApiEndpoint: React.FC = () => {
  // State management
  const [configs, setConfigs] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingConfigs, setIsLoadingConfigs] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(12);
  const [searchFilters, setSearchFilters] = useState({
    api_name: "",
  });

  // Form modal state
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState<ApiEndpointType | null>(
    null,
  );

  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [configToDelete, setConfigToDelete] = useState<ApiEndpointType | null>(
    null,
  );

  const isInitialMount = useRef(true);

  // Fetch data on mount and filter changes
  useEffect(() => {
    if (isInitialMount.current) {
      fetchApiEndpoints();
      isInitialMount.current = false;
    } else if (Object.values(searchFilters).some((filter) => filter !== "")) {
      const debounceTimer = setTimeout(() => {
        fetchApiEndpoints();
      }, 500);
      return () => clearTimeout(debounceTimer);
    } else {
      fetchApiEndpoints();
    }
  }, [currentPage, searchFilters]);

  // Fetch configs function
  const fetchApiEndpoints = async () => {
    try {
      setIsLoadingConfigs(true);
      const params: ApiEndpointListParams = {
        skip: (currentPage - 1) * pageSize,
        limit: pageSize,
        ...(searchFilters.api_name && {
          api_name: searchFilters.api_name,
        }),
      };

      const response = await apiEndpointService.getApiEndpoints(params);
      setConfigs(response.data.configs || []);
      setTotalCount(response.data.total_count || 0);
    } catch (error) {
      console.error("Error fetching API endpoints:", error);
      toast.error("Failed to load API endpoints");
    } finally {
      setIsLoadingConfigs(false);
    }
  };

  // Fetch config statistics

  // Create config handler
  const handleCreateConfig = () => {
    setSelectedConfig(null);
    setIsFormOpen(true);
  };

  // Edit config handler
  const handleEditConfig = (config: ApiEndpointType) => {
    setSelectedConfig(config);
    setIsFormOpen(true);
  };

  // Close form handler
  const handleCloseForm = () => {
    setIsFormOpen(false);
    setSelectedConfig(null);
  };

  // Form submit handler
  const handleFormSubmit = async (
    data: CreateApiEndpointData | UpdateApiEndpointData,
  ) => {
    setIsLoading(true);
    try {
      if (selectedConfig) {
        // Update existing config
        await apiEndpointService.updateApiEndpoint(
          selectedConfig._id,
          data as UpdateApiEndpointData,
        );
        handleCloseForm();
        toast.success(`API Endpoint updated successfully!`);
        await fetchApiEndpoints();
      } else {
        // Create new config
        await apiEndpointService.createApiEndpoint(data as any);
        handleCloseForm();
        toast.success(`API Endpoint created successfully!`);
        await fetchApiEndpoints();
      }
    } catch (error: any) {
      console.error("Error saving API endpoint:", error);
      const errorMessage =
        error.response?.data?.error?.error_description ||
        error.response?.data?.message ||
        (selectedConfig
          ? "Failed to update API endpoint"
          : "Failed to create API endpoint");
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Delete confirmation handlercan
  const handleDeleteClick = (config: ApiEndpointType) => {
    setConfigToDelete(config);
    console.log(config);

    setShowDeleteConfirm(true);
  };

  // Confirm delete handler
  const handleConfirmDelete = async () => {
    if (configToDelete) {
      try {
        await apiEndpointService.deleteApiEndpoint(configToDelete._id);
        setConfigToDelete(null);
        setShowDeleteConfirm(false);
        toast.success(`API Endpoint deleted successfully!`);
        await fetchApiEndpoints();
      } catch (error: any) {
        console.error("Error deleting API endpoint:", error);
        const errorMessage =
          error.response?.data?.error?.error_description ||
          error.response?.data?.message ||
          "Failed to delete API endpoint";
        toast.error(errorMessage);
      }
    }
  };

  // Cancel delete handler
  const handleCancelDelete = () => {
    setConfigToDelete(null);
    setShowDeleteConfirm(false);
  };

  // Search handler
  const handleSearchChange = (field: string, value: string) => {
    setSearchFilters((prev) => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  };

  // Pagination handler
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Pagination calculations
  const totalPages = Math.ceil(totalCount / pageSize);
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  return (
    <div>
      <div className="">
        {/* Slim toolbar: search + create */}
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="relative w-full max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search by name..."
              className="pl-10 pr-9 py-2 w-full border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              value={searchFilters.api_name}
              onChange={(e) => handleSearchChange("api_name", e.target.value)}
            />
            {searchFilters.api_name && (
              <button
                type="button"
                onClick={() => handleSearchChange("api_name", "")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <Can permission="integrations:create">
            <button
              onClick={handleCreateConfig}
              disabled={isLoading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium flex-shrink-0"
              type="button"
            >
              <Plus className="w-4 h-4" />
              Create API Endpoint
            </button>
          </Can>
        </div>

        {/* Configs Content */}
        {isLoadingConfigs ? (
          <div className="flex flex-col items-center justify-center min-h-[400px]">
            <Loader />
          </div>
        ) : configs.length > 0 ? (
          <>
            <ListView
              configs={configs}
              onEdit={handleEditConfig}
              onDelete={handleDeleteClick}
            />

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing <span className="font-medium">{startItem}</span> to{" "}
                  <span className="font-medium">{endItem}</span> of{" "}
                  <span className="font-medium">{totalCount}</span> endpoints
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>

                  {/* Page Numbers */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          currentPage === pageNum
                            ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                            : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
            <Plug className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No API Endpoints found
            </h3>
            <p className="text-gray-500 mb-6">
              {Object.values(searchFilters).some((filter) => filter !== "")
                ? "No API endpoints match your current search criteria. Try adjusting your filters."
                : "Get started by creating your first API endpoint."}
            </p>
            <Can permission="integrations:create">
              <button
                onClick={handleCreateConfig}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg flex items-center gap-2 mx-auto transition-colors duration-150"
              >
                <Plus className="w-4 h-4" />
                Create API Endpoint
              </button>
            </Can>
          </div>
        )}
      </div>

      {/* API Endpoint Form Modal */}
      <ApiEndpointForm
        isOpen={isFormOpen}
        onClose={handleCloseForm}
        config={selectedConfig as any}
        onSubmit={handleFormSubmit as any}
        isLoading={isLoading}
      />

      {/* Confirmation Modal for Delete */}
      <ConfirmationDialog
        isOpen={showDeleteConfirm}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete API Endpoint"
        confirmLabel="Delete"
        cancelLabel="Cancel"
        description={`Are you sure you want to delete the API Endpoint <strong>${configToDelete?.api_name}</strong>? This action cannot be undone.`}
        iconName="warning"
      />
    </div>
  );
};

export default ApiEndpoint;
